import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useToast } from '../contexts/ToastContext';
import { formatQty } from '../lib/quantity';
import {
  officeContractcalcApi,
  type ContractCalcBasic,
  type ContractCalcLine,
  type ContractCalcSummary,
} from '../services/officeContractcalc';

const APP_HEADER_PX = 92;

type EditableCalc = {
  id: number;
  customerName: string;
  year: string;
  vatFoodSnapshot: number;
  bulkPriceKgSnapshot: number;
  ownCostKgSnapshot: number;
  basicUpdatedAtAtSave: string | null;
  updatedAt: string | null;
  isOutdated: boolean;
  lines: ContractCalcLine[];
};

type MoneyValues = {
  vatFood: number;
  bulkKg: number;
  ownKg: number;
};

type LineDraft = {
  index: number | null;
  lineType: 'HONEY' | 'OTHER';
  title: string;
  honeyKgPerUnit: string;
  unitsPerYear: string;
  packagingCostPerUnit: string;
  sellPriceExVatPerUnit: string;
  qty: string;
  unitPriceExVat: string;
  direction: 'INCOME' | 'COST';
};

type Totals = {
  honeyNeedKg: number;
  profitOwnEx: number;
  profitBuyEx: number;
  vatFoodIncome: number;
  vatOtherIncome: number;
  incomeEx: number;
  incomeInc: number;
};

type CalcPickerState = {
  open: boolean;
  mandatory: boolean;
};

const EMPTY_TOTALS: Totals = {
  honeyNeedKg: 0,
  profitOwnEx: 0,
  profitBuyEx: 0,
  vatFoodIncome: 0,
  vatOtherIncome: 0,
  incomeEx: 0,
  incomeInc: 0,
};

function parseLocaleDecimal(value: string): number {
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function decimalString(value: number, digits = 2): string {
  return value.toFixed(digits).replace('.', ',');
}

function formatMoney(value: number): string {
  return `${value.toFixed(2)} kr`;
}

function formatValue(value: number): string {
  return value.toFixed(2);
}

function formatVatLabel(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function createEmptyCalc(basic: ContractCalcBasic): EditableCalc {
  return {
    id: 0,
    customerName: '',
    year: String(new Date().getFullYear()),
    vatFoodSnapshot: basic.vatFoodDefault,
    bulkPriceKgSnapshot: basic.bulkPriceKgDefault,
    ownCostKgSnapshot: basic.ownCostKgDefault,
    basicUpdatedAtAtSave: basic.updatedAt,
    updatedAt: null,
    isOutdated: false,
    lines: [],
  };
}

function createLineDraft(lineType: 'HONEY' | 'OTHER', line?: ContractCalcLine, index: number | null = null): LineDraft {
  return {
    index,
    lineType,
    title: line?.title ?? '',
    honeyKgPerUnit: decimalString(line?.honeyKgPerUnit ?? 0, 4),
    unitsPerYear: String(line?.unitsPerYear ?? 0),
    packagingCostPerUnit: decimalString(line?.packagingCostPerUnit ?? 0),
    sellPriceExVatPerUnit: decimalString(line?.sellPriceExVatPerUnit ?? 0),
    qty: decimalString(line?.qty ?? 0, 4),
    unitPriceExVat: decimalString(line?.unitPriceExVat ?? 0),
    direction: line?.direction ?? 'INCOME',
  };
}

function buildLineFromDraft(draft: LineDraft, sortOrder: number): ContractCalcLine {
  return {
    sortOrder,
    lineType: draft.lineType,
    title: draft.title.trim(),
    honeyKgPerUnit: parseLocaleDecimal(draft.honeyKgPerUnit),
    unitsPerYear: Math.max(0, Math.round(parseLocaleDecimal(draft.unitsPerYear))),
    packagingCostPerUnit: parseLocaleDecimal(draft.packagingCostPerUnit),
    sellPriceExVatPerUnit: parseLocaleDecimal(draft.sellPriceExVatPerUnit),
    qty: parseLocaleDecimal(draft.qty),
    unitPriceExVat: parseLocaleDecimal(draft.unitPriceExVat),
    direction: draft.direction,
  };
}

function computeHoneyLine(line: ContractCalcLine, values: MoneyValues) {
  const costPerUnitOwnEx = line.honeyKgPerUnit * values.ownKg + line.packagingCostPerUnit;
  const costPerUnitBuyEx = line.honeyKgPerUnit * values.bulkKg + line.packagingCostPerUnit;
  const priceIncPerUnit = line.sellPriceExVatPerUnit * (1 + values.vatFood);
  const profitOwnEx = line.sellPriceExVatPerUnit - costPerUnitOwnEx;
  const profitOwnInc = priceIncPerUnit - costPerUnitOwnEx;
  const profitBuyEx = line.sellPriceExVatPerUnit - costPerUnitBuyEx;
  const profitBuyInc = priceIncPerUnit - costPerUnitBuyEx;
  return {
    costPerUnitOwnEx,
    costPerUnitBuyEx,
    priceIncPerUnit,
    profitOwnEx,
    profitOwnInc,
    profitBuyEx,
    profitBuyInc,
    yearlyOwnEx: profitOwnEx * line.unitsPerYear,
    yearlyOwnInc: profitOwnInc * line.unitsPerYear,
    yearlyBuyEx: profitBuyEx * line.unitsPerYear,
    yearlyBuyInc: profitBuyInc * line.unitsPerYear,
  };
}

function computeOtherLine(line: ContractCalcLine) {
  const sign = line.direction === 'COST' ? -1 : 1;
  const ex = sign * line.qty * line.unitPriceExVat;
  const inc = ex * 1.25;
  const vat = inc - ex;
  return { ex, inc, vat };
}

function summarize(lines: ContractCalcLine[], values: MoneyValues): Totals {
  return lines.reduce<Totals>((totals, line) => {
    if (line.lineType === 'HONEY') {
      const honey = computeHoneyLine(line, values);
      const revenueEx = line.sellPriceExVatPerUnit * line.unitsPerYear;
      const revenueInc = honey.priceIncPerUnit * line.unitsPerYear;
      return {
        honeyNeedKg: totals.honeyNeedKg + line.honeyKgPerUnit * line.unitsPerYear,
        profitOwnEx: totals.profitOwnEx + honey.yearlyOwnEx,
        profitBuyEx: totals.profitBuyEx + honey.yearlyBuyEx,
        vatFoodIncome: totals.vatFoodIncome + (revenueInc - revenueEx),
        vatOtherIncome: totals.vatOtherIncome,
        incomeEx: totals.incomeEx + revenueEx,
        incomeInc: totals.incomeInc + revenueInc,
      };
    }

    const other = computeOtherLine(line);
    return {
      honeyNeedKg: totals.honeyNeedKg,
      profitOwnEx: totals.profitOwnEx,
      profitBuyEx: totals.profitBuyEx,
      vatFoodIncome: totals.vatFoodIncome,
      vatOtherIncome: totals.vatOtherIncome + other.vat,
      incomeEx: totals.incomeEx + other.ex,
      incomeInc: totals.incomeInc + other.inc,
    };
  }, EMPTY_TOTALS);
}

function ToolbarButton({
  children,
  className,
  onClick,
  disabled = false,
}: {
  children: ReactNode;
  className: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-10 min-w-10 items-center justify-center rounded-xl px-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

function Modal({
  title,
  children,
  onClose,
  hideClose = false,
}: {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  hideClose?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-3">
      <div className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-950">{title}</h2>
          {!hideClose && onClose && (
            <button type="button" className="btn btn-secondary px-2 py-1" onClick={onClose}>
              Stäng
            </button>
          )}
        </div>
        <div className="overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
}

export function OfficeContractcalcPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [basic, setBasic] = useState<ContractCalcBasic | null>(null);
  const [calcSummaries, setCalcSummaries] = useState<ContractCalcSummary[]>([]);
  const [currentCalc, setCurrentCalc] = useState<EditableCalc | null>(null);
  const [picker, setPicker] = useState<CalcPickerState>({ open: true, mandatory: true });
  const [useCurrentBasic, setUseCurrentBasic] = useState(false);
  const [basicModalOpen, setBasicModalOpen] = useState(false);
  const [calcValuesModalOpen, setCalcValuesModalOpen] = useState(false);
  const [lineDraft, setLineDraft] = useState<LineDraft | null>(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [basicForm, setBasicForm] = useState({ vatFoodDefault: '12', bulkPriceKgDefault: '0,00', ownCostKgDefault: '0,00' });
  const [calcValuesForm, setCalcValuesForm] = useState({ vatFoodSnapshot: '12', bulkPriceKgSnapshot: '0,00', ownCostKgSnapshot: '0,00' });
  const [copyForm, setCopyForm] = useState({ newCustomerName: '', newYear: String(new Date().getFullYear()), useCurrentBasic: true });

  const loadList = async () => {
    const response = await officeContractcalcApi.list();
    setBasic(response.basic);
    setCalcSummaries(response.calcs);
    return response;
  };

  useEffect(() => {
    loadList()
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Kunde inte ladda kalkyler');
      })
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    if (!basic) {
      return;
    }
    setBasicForm({
      vatFoodDefault: String(Math.round(basic.vatFoodDefault * 100)),
      bulkPriceKgDefault: decimalString(basic.bulkPriceKgDefault),
      ownCostKgDefault: decimalString(basic.ownCostKgDefault),
    });
  }, [basic]);

  useEffect(() => {
    if (!currentCalc) {
      return;
    }
    setCalcValuesForm({
      vatFoodSnapshot: String(Math.round(currentCalc.vatFoodSnapshot * 100)),
      bulkPriceKgSnapshot: decimalString(currentCalc.bulkPriceKgSnapshot),
      ownCostKgSnapshot: decimalString(currentCalc.ownCostKgSnapshot),
    });
  }, [currentCalc]);

  const activeValues = useMemo<MoneyValues>(() => {
    if (!basic || !currentCalc) {
      return { vatFood: 0.12, bulkKg: 0, ownKg: 0 };
    }
    if (useCurrentBasic) {
      return {
        vatFood: basic.vatFoodDefault,
        bulkKg: basic.bulkPriceKgDefault,
        ownKg: basic.ownCostKgDefault,
      };
    }
    return {
      vatFood: currentCalc.vatFoodSnapshot,
      bulkKg: currentCalc.bulkPriceKgSnapshot,
      ownKg: currentCalc.ownCostKgSnapshot,
    };
  }, [basic, currentCalc, useCurrentBasic]);

  const savedValues = useMemo<MoneyValues>(() => {
    if (!currentCalc) {
      return { vatFood: 0.12, bulkKg: 0, ownKg: 0 };
    }
    return {
      vatFood: currentCalc.vatFoodSnapshot,
      bulkKg: currentCalc.bulkPriceKgSnapshot,
      ownKg: currentCalc.ownCostKgSnapshot,
    };
  }, [currentCalc]);

  const currentBasicValues = useMemo<MoneyValues>(() => {
    if (!basic) {
      return { vatFood: 0.12, bulkKg: 0, ownKg: 0 };
    }
    return {
      vatFood: basic.vatFoodDefault,
      bulkKg: basic.bulkPriceKgDefault,
      ownKg: basic.ownCostKgDefault,
    };
  }, [basic]);

  const totalsSaved = useMemo(() => summarize(currentCalc?.lines ?? [], savedValues), [currentCalc, savedValues]);
  const totalsNow = useMemo(() => summarize(currentCalc?.lines ?? [], currentBasicValues), [currentCalc, currentBasicValues]);

  const openPicker = () => setPicker({ open: true, mandatory: currentCalc === null });

  const openExisting = async (id: number) => {
    try {
      const response = await officeContractcalcApi.get(id);
      setBasic(response.basic);
      setCurrentCalc({
        id: response.calc.id,
        customerName: response.calc.customerName,
        year: String(response.calc.year),
        vatFoodSnapshot: response.calc.vatFoodSnapshot,
        bulkPriceKgSnapshot: response.calc.bulkPriceKgSnapshot,
        ownCostKgSnapshot: response.calc.ownCostKgSnapshot,
        basicUpdatedAtAtSave: response.calc.basicUpdatedAtAtSave,
        updatedAt: response.calc.updatedAt,
        isOutdated: response.calc.isOutdated,
        lines: response.lines,
      });
      setUseCurrentBasic(false);
      setPicker({ open: false, mandatory: false });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte öppna kalkyl');
    }
  };

  const createNew = () => {
    if (!basic) {
      return;
    }
    setCurrentCalc(createEmptyCalc(basic));
    setUseCurrentBasic(false);
    setPicker({ open: false, mandatory: false });
  };

  const refreshList = async () => {
    const response = await loadList();
    if (currentCalc && currentCalc.id > 0) {
      const summary = response.calcs.find((item) => item.id === currentCalc.id);
      if (summary) {
        setCurrentCalc((prev) =>
          prev
            ? {
                ...prev,
                isOutdated: summary.isOutdated,
                updatedAt: summary.updatedAt,
              }
            : prev
        );
      }
    }
  };

  const saveCalc = async () => {
    if (!currentCalc) {
      return;
    }
    if (!currentCalc.customerName.trim()) {
      toast.error('Kund/namn krävs');
      return;
    }

    setSaving(true);
    try {
      const response = await officeContractcalcApi.save({
        id: currentCalc.id,
        customerName: currentCalc.customerName.trim(),
        year: Number(currentCalc.year),
        vatFoodSnapshot: currentCalc.vatFoodSnapshot,
        bulkPriceKgSnapshot: currentCalc.bulkPriceKgSnapshot,
        ownCostKgSnapshot: currentCalc.ownCostKgSnapshot,
        basicUpdatedAtAtSave: currentCalc.basicUpdatedAtAtSave,
        lines: currentCalc.lines.map((line, index) => ({ ...line, sortOrder: (index + 1) * 10 })),
      });
      await refreshList();
      await openExisting(response.id);
      toast.success('Kalkyl sparad');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara kalkyl');
    } finally {
      setSaving(false);
    }
  };

  const saveBasic = async () => {
    try {
      const vatFoodDefault = parseLocaleDecimal(basicForm.vatFoodDefault) / 100;
      const response = await officeContractcalcApi.saveBasic({
        vatFoodDefault,
        bulkPriceKgDefault: parseLocaleDecimal(basicForm.bulkPriceKgDefault),
        ownCostKgDefault: parseLocaleDecimal(basicForm.ownCostKgDefault),
      });
      setBasic(response.basic);
      setBasicModalOpen(false);
      await refreshList();
      toast.success('Standardvärden sparade');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara standardvärden');
    }
  };

  const saveCalcValues = () => {
    if (!currentCalc) {
      return;
    }
    const nextVat = parseLocaleDecimal(calcValuesForm.vatFoodSnapshot) / 100;
    if (nextVat !== 0.12 && nextVat !== 0.06) {
      toast.error('Livsmedelsmoms måste vara 12 eller 6');
      return;
    }
    setCurrentCalc({
      ...currentCalc,
      vatFoodSnapshot: nextVat,
      bulkPriceKgSnapshot: parseLocaleDecimal(calcValuesForm.bulkPriceKgSnapshot),
      ownCostKgSnapshot: parseLocaleDecimal(calcValuesForm.ownCostKgSnapshot),
    });
    setCalcValuesModalOpen(false);
  };

  const applyBasicToCalc = async () => {
    if (!currentCalc || currentCalc.id <= 0) {
      return;
    }
    try {
      const response = await officeContractcalcApi.applyBasic(currentCalc.id);
      setBasic(response.basic);
      await openExisting(currentCalc.id);
      toast.success('Kalkylens värden uppdaterade');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera kalkylens värden');
    }
  };

  const saveLine = () => {
    if (!currentCalc || !lineDraft) {
      return;
    }
    if (!lineDraft.title.trim()) {
      toast.error('Radens titel krävs');
      return;
    }

    const nextLine = buildLineFromDraft(lineDraft, ((lineDraft.index ?? currentCalc.lines.length) + 1) * 10);
    const nextLines = [...currentCalc.lines];
    if (lineDraft.index === null) {
      nextLines.push(nextLine);
    } else {
      nextLines[lineDraft.index] = nextLine;
    }
    setCurrentCalc({ ...currentCalc, lines: nextLines.map((line, index) => ({ ...line, sortOrder: (index + 1) * 10 })) });
    setLineDraft(null);
  };

  const deleteLine = (index: number) => {
    if (!currentCalc) {
      return;
    }
    setCurrentCalc({
      ...currentCalc,
      lines: currentCalc.lines.filter((_, currentIndex) => currentIndex !== index).map((line, currentIndex) => ({
        ...line,
        sortOrder: (currentIndex + 1) * 10,
      })),
    });
  };

  const deleteCalc = async () => {
    if (!currentCalc || currentCalc.id <= 0) {
      setCurrentCalc(basic ? createEmptyCalc(basic) : null);
      setDeleteModalOpen(false);
      return;
    }
    try {
      await officeContractcalcApi.delete(currentCalc.id);
      toast.success('Kalkyl borttagen');
      setDeleteModalOpen(false);
      await refreshList();
      setCurrentCalc(null);
      setPicker({ open: true, mandatory: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort kalkyl');
    }
  };

  const copyCalc = async () => {
    if (!currentCalc || currentCalc.id <= 0) {
      toast.error('Spara kalkylen innan du kopierar den');
      return;
    }
    try {
      const response = await officeContractcalcApi.copy(currentCalc.id, {
        newCustomerName: copyForm.newCustomerName.trim(),
        newYear: Math.round(parseLocaleDecimal(copyForm.newYear)),
        useCurrentBasic: copyForm.useCurrentBasic,
      });
      setCopyModalOpen(false);
      await refreshList();
      await openExisting(response.id);
      toast.success('Kalkyl kopierad');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte kopiera kalkyl');
    }
  };

  const openCopyModal = () => {
    if (!currentCalc) {
      return;
    }
    setCopyForm({
      newCustomerName: currentCalc.customerName ? `${currentCalc.customerName} kopia` : '',
      newYear: currentCalc.year,
      useCurrentBasic: true,
    });
    setCopyModalOpen(true);
  };

  if (loading || !basic) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  const containerStyle = { height: `calc(100dvh - ${APP_HEADER_PX}px)` };

  return (
    <div className="-mx-3" style={containerStyle}>
      <div className="flex h-full flex-col bg-white">
        <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-3">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-lg font-semibold text-slate-950">Kalkyl</div>
              <div className="flex flex-wrap items-center gap-2">
                <ToolbarButton className="border border-slate-300 bg-white text-slate-700" onClick={openPicker}>
                  Öppna
                </ToolbarButton>
                <ToolbarButton className="border border-slate-300 bg-white text-slate-700" onClick={() => setBasicModalOpen(true)}>
                  Standardvärden
                </ToolbarButton>
                <ToolbarButton className="bg-success text-white" onClick={saveCalc} disabled={!currentCalc || saving}>
                  {saving ? 'Sparar' : 'Spara'}
                </ToolbarButton>
                <ToolbarButton className="bg-orange-500 text-white" onClick={openCopyModal} disabled={!currentCalc || currentCalc.id <= 0}>
                  Kopiera
                </ToolbarButton>
                <ToolbarButton className="bg-danger text-white" onClick={() => setDeleteModalOpen(true)} disabled={!currentCalc}>
                  Ta bort
                </ToolbarButton>
                <ToolbarButton className="bg-success text-white" onClick={createNew}>
                  +
                </ToolbarButton>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                className="flex-1"
                value={currentCalc?.customerName ?? ''}
                onChange={(event) => currentCalc && setCurrentCalc({ ...currentCalc, customerName: event.target.value })}
                placeholder="Kund/namn"
              />
              <input
                className="w-20"
                value={currentCalc?.year ?? ''}
                onChange={(event) => currentCalc && setCurrentCalc({ ...currentCalc, year: event.target.value })}
                placeholder="År"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="text-sm text-slate-600">Standard:</div>
              <div className="inline-flex rounded-xl border border-slate-300 bg-white p-1">
                <button
                  type="button"
                  className={`rounded-lg px-3 py-1 text-sm ${!useCurrentBasic ? 'bg-slate-900 text-white' : 'text-slate-700'}`}
                  onClick={() => setUseCurrentBasic(false)}
                >
                  Sparat
                </button>
                <button
                  type="button"
                  className={`rounded-lg px-3 py-1 text-sm ${useCurrentBasic ? 'bg-slate-900 text-white' : 'text-slate-700'}`}
                  onClick={() => setUseCurrentBasic(true)}
                >
                  (nu)
                </button>
              </div>
              <ToolbarButton className="border border-slate-300 bg-white text-slate-700" onClick={() => setCalcValuesModalOpen(true)} disabled={!currentCalc}>
                Värden
              </ToolbarButton>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-600">Beräknas med:</span>
              <span className="rounded-full border border-slate-300 px-3 py-1">Bulk {formatMoney(activeValues.bulkKg)}</span>
              <span className="rounded-full border border-slate-300 px-3 py-1">Egen {formatMoney(activeValues.ownKg)}</span>
              <span className="rounded-full border border-slate-300 px-3 py-1">Livsmedelsmoms {formatVatLabel(activeValues.vatFood)}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-3 py-3">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700"
                onClick={() => setLineDraft(createLineDraft('HONEY'))}
                disabled={!currentCalc}
              >
                + Honung
              </button>
              <button
                type="button"
                className="rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700"
                onClick={() => setLineDraft(createLineDraft('OTHER'))}
                disabled={!currentCalc}
              >
                + Övrigt
              </button>
            </div>

            {currentCalc && currentCalc.lines.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500">
                Inga rader ännu. Lägg till honung eller övrigt.
              </div>
            )}

            {currentCalc?.lines.map((line, index) => {
              const honey = line.lineType === 'HONEY' ? computeHoneyLine(line, activeValues) : null;
              const other = line.lineType === 'OTHER' ? computeOtherLine(line) : null;
              return (
                <div key={`${line.lineType}-${index}`} className="rounded-2xl border border-orange-200 bg-orange-50/30 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-slate-950">{line.title}</div>
                      <div className="text-sm text-slate-600">
                        {line.lineType === 'HONEY' ? `Honung • moms ${formatVatLabel(activeValues.vatFood)}` : 'Övrigt • moms 25%'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-xl bg-orange-400 px-3 py-2 text-sm font-semibold text-white"
                        onClick={() => setLineDraft(createLineDraft(line.lineType, line, index))}
                      >
                        Redigera
                      </button>
                      <button
                        type="button"
                        className="rounded-xl bg-danger px-3 py-2 text-sm font-semibold text-white"
                        onClick={() => deleteLine(index)}
                      >
                        X
                      </button>
                    </div>
                  </div>

                  {line.lineType === 'HONEY' && honey && (
                    <div className="mt-3 space-y-2 text-sm text-slate-700">
                      <div>{formatQty(line.honeyKgPerUnit, 'kg')} kg/st • {line.unitsPerYear} st/år</div>
                      {line.packagingCostPerUnit > 0 && <div>Emballagekostnad: {formatMoney(line.packagingCostPerUnit)}/st</div>}
                      <div>Pris exkl. moms/st: {formatMoney(line.sellPriceExVatPerUnit)}</div>
                      <div className="space-y-1">
                        <div className="font-medium text-slate-900">Tjänst per styck (ex / inkl)</div>
                        <div>Egen: {formatValue(honey.profitOwnEx)} / {formatValue(honey.profitOwnInc)}</div>
                        <div>Inköpt: {formatValue(honey.profitBuyEx)} / {formatValue(honey.profitBuyInc)}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-slate-900">Vinst per år (ex / inkl)</div>
                        <div>Egen: {formatValue(honey.yearlyOwnEx)} / {formatValue(honey.yearlyOwnInc)}</div>
                        <div>Inköpt: {formatValue(honey.yearlyBuyEx)} / {formatValue(honey.yearlyBuyInc)}</div>
                      </div>
                    </div>
                  )}

                  {line.lineType === 'OTHER' && other && (
                    <div className="mt-3 space-y-2 text-sm text-slate-700">
                      <div>Antal: {formatQty(line.qty)}</div>
                      <div>Pris ex moms/st: {formatMoney(line.unitPriceExVat)}</div>
                      <div>{line.direction === 'COST' ? 'Kostnad' : 'Intäkt'}</div>
                      <div className="space-y-1">
                        <div className="font-medium text-slate-900">Radnetto (ex / inkl)</div>
                        <div>{formatValue(other.ex)} / {formatValue(other.inc)}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-3">
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
            <div>
              <div className="text-slate-500">Honungsbehov (kg)</div>
              <div>{formatValue(totalsSaved.honeyNeedKg)} <span className="text-slate-400">({formatValue(totalsNow.honeyNeedKg)})</span></div>
            </div>
            <div>
              <div className="text-slate-500">Vinst ex (egen)</div>
              <div>{formatValue(totalsSaved.profitOwnEx)} <span className="text-slate-400">({formatValue(totalsNow.profitOwnEx)})</span></div>
            </div>
            <div>
              <div className="text-slate-500">Vinst ex (inköp)</div>
              <div>{formatValue(totalsSaved.profitBuyEx)} <span className="text-slate-400">({formatValue(totalsNow.profitBuyEx)})</span></div>
            </div>
            <div>
              <div className="text-slate-500">Moms (intäkt) livsmedel + övrigt</div>
              <div>
                {formatValue(totalsSaved.vatFoodIncome)} + {formatValue(totalsSaved.vatOtherIncome)}{' '}
                <span className="text-slate-400">({formatValue(totalsNow.vatFoodIncome)} + {formatValue(totalsNow.vatOtherIncome)})</span>
              </div>
            </div>
            <div>
              <div className="text-slate-500">Intäkt ex / inkl</div>
              <div>
                {formatValue(totalsSaved.incomeEx)} / {formatValue(totalsSaved.incomeInc)}{' '}
                <span className="text-slate-400">({formatValue(totalsNow.incomeEx)} / {formatValue(totalsNow.incomeInc)})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {picker.open && (
        <Modal title="Öppna kalkyl" onClose={picker.mandatory ? undefined : () => setPicker({ open: false, mandatory: false })} hideClose={picker.mandatory}>
          <div className="space-y-3">
            <div className="flex justify-end">
              <ToolbarButton className="bg-success text-white" onClick={createNew}>
                + Ny kalkyl
              </ToolbarButton>
            </div>
            <div className="space-y-2">
              {calcSummaries.map((calc) => (
                <button
                  key={calc.id}
                  type="button"
                  className="flex w-full items-start justify-between rounded-2xl border border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                  onClick={() => void openExisting(calc.id)}
                >
                  <div>
                    <div className="font-semibold text-slate-950">{calc.customerName}</div>
                    <div className="text-sm text-slate-600">{calc.year}</div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="text-slate-500">{String(calc.updatedAt).slice(0, 10)}</div>
                    {calc.isOutdated && <div className="text-orange-600">Standardvärden uppdaterade</div>}
                  </div>
                </button>
              ))}
              {calcSummaries.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500">
                  Inga sparade kalkyler ännu.
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {basicModalOpen && (
        <Modal title="Standardvärden" onClose={() => setBasicModalOpen(false)}>
          <div className="space-y-3">
            <label className="block text-sm">
              Livsmedelsmoms
              <select className="mt-1 w-full" value={basicForm.vatFoodDefault} onChange={(event) => setBasicForm({ ...basicForm, vatFoodDefault: event.target.value })}>
                <option value="12">12</option>
                <option value="6">6</option>
              </select>
            </label>
            <label className="block text-sm">
              Bulkpris kr/kg
              <input className="mt-1 w-full" value={basicForm.bulkPriceKgDefault} onChange={(event) => setBasicForm({ ...basicForm, bulkPriceKgDefault: event.target.value })} />
            </label>
            <label className="block text-sm">
              Egen kostnad kr/kg
              <input className="mt-1 w-full" value={basicForm.ownCostKgDefault} onChange={(event) => setBasicForm({ ...basicForm, ownCostKgDefault: event.target.value })} />
            </label>
            <div className="flex justify-end">
              <ToolbarButton className="bg-success text-white" onClick={() => void saveBasic()}>
                Spara
              </ToolbarButton>
            </div>
          </div>
        </Modal>
      )}

      {calcValuesModalOpen && currentCalc && (
        <Modal title="Kalkylens värden" onClose={() => setCalcValuesModalOpen(false)}>
          <div className="space-y-3">
            {currentCalc.isOutdated && (
              <div className="rounded-2xl border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-900">
                Standardvärden har ändrats sedan kalkylen sparades.
                <div className="mt-2">
                  <ToolbarButton className="bg-orange-500 text-white" onClick={() => void applyBasicToCalc()}>
                    Använd standardvärden (nu)
                  </ToolbarButton>
                </div>
              </div>
            )}
            <label className="block text-sm">
              Livsmedelsmoms
              <select className="mt-1 w-full" value={calcValuesForm.vatFoodSnapshot} onChange={(event) => setCalcValuesForm({ ...calcValuesForm, vatFoodSnapshot: event.target.value })}>
                <option value="12">12</option>
                <option value="6">6</option>
              </select>
            </label>
            <label className="block text-sm">
              Bulkpris kr/kg
              <input className="mt-1 w-full" value={calcValuesForm.bulkPriceKgSnapshot} onChange={(event) => setCalcValuesForm({ ...calcValuesForm, bulkPriceKgSnapshot: event.target.value })} />
            </label>
            <label className="block text-sm">
              Egen kostnad kr/kg
              <input className="mt-1 w-full" value={calcValuesForm.ownCostKgSnapshot} onChange={(event) => setCalcValuesForm({ ...calcValuesForm, ownCostKgSnapshot: event.target.value })} />
            </label>
            <div className="flex justify-end">
              <ToolbarButton className="bg-success text-white" onClick={saveCalcValues}>
                Spara
              </ToolbarButton>
            </div>
          </div>
        </Modal>
      )}

      {lineDraft && (
        <Modal title={lineDraft.index === null ? 'Ny rad' : 'Redigera rad'} onClose={() => setLineDraft(null)}>
          <div className="space-y-3">
            <label className="block text-sm">
              Titel
              <input className="mt-1 w-full" value={lineDraft.title} onChange={(event) => setLineDraft({ ...lineDraft, title: event.target.value })} />
            </label>

            {lineDraft.lineType === 'HONEY' ? (
              <>
                <label className="block text-sm">
                  Kg per styck
                  <input className="mt-1 w-full" value={lineDraft.honeyKgPerUnit} onChange={(event) => setLineDraft({ ...lineDraft, honeyKgPerUnit: event.target.value })} />
                </label>
                <label className="block text-sm">
                  Styck per år
                  <input className="mt-1 w-full" value={lineDraft.unitsPerYear} onChange={(event) => setLineDraft({ ...lineDraft, unitsPerYear: event.target.value })} />
                </label>
                <label className="block text-sm">
                  Emballagekostnad kr/st
                  <input className="mt-1 w-full" value={lineDraft.packagingCostPerUnit} onChange={(event) => setLineDraft({ ...lineDraft, packagingCostPerUnit: event.target.value })} />
                </label>
                <label className="block text-sm">
                  Pris ex moms/st
                  <input className="mt-1 w-full" value={lineDraft.sellPriceExVatPerUnit} onChange={(event) => setLineDraft({ ...lineDraft, sellPriceExVatPerUnit: event.target.value })} />
                </label>
              </>
            ) : (
              <>
                <label className="block text-sm">
                  Antal
                  <input className="mt-1 w-full" value={lineDraft.qty} onChange={(event) => setLineDraft({ ...lineDraft, qty: event.target.value })} />
                </label>
                <label className="block text-sm">
                  Pris ex moms/st
                  <input className="mt-1 w-full" value={lineDraft.unitPriceExVat} onChange={(event) => setLineDraft({ ...lineDraft, unitPriceExVat: event.target.value })} />
                </label>
                <label className="block text-sm">
                  Typ
                  <select className="mt-1 w-full" value={lineDraft.direction} onChange={(event) => setLineDraft({ ...lineDraft, direction: event.target.value as 'INCOME' | 'COST' })}>
                    <option value="INCOME">Intäkt</option>
                    <option value="COST">Kostnad</option>
                  </select>
                </label>
              </>
            )}

            <div className="flex justify-end">
              <ToolbarButton className="bg-success text-white" onClick={saveLine}>
                Spara rad
              </ToolbarButton>
            </div>
          </div>
        </Modal>
      )}

      {copyModalOpen && (
        <Modal title="Kopiera kalkyl" onClose={() => setCopyModalOpen(false)}>
          <div className="space-y-3">
            <label className="block text-sm">
              Kund/namn
              <input className="mt-1 w-full" value={copyForm.newCustomerName} onChange={(event) => setCopyForm({ ...copyForm, newCustomerName: event.target.value })} />
            </label>
            <label className="block text-sm">
              År
              <input className="mt-1 w-full" value={copyForm.newYear} onChange={(event) => setCopyForm({ ...copyForm, newYear: event.target.value })} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={copyForm.useCurrentBasic} onChange={(event) => setCopyForm({ ...copyForm, useCurrentBasic: event.target.checked })} />
              Använd standardvärden (nu)
            </label>
            <div className="flex justify-end">
              <ToolbarButton className="bg-orange-500 text-white" onClick={() => void copyCalc()}>
                Kopiera
              </ToolbarButton>
            </div>
          </div>
        </Modal>
      )}

      {deleteModalOpen && (
        <Modal title="Ta bort kalkyl" onClose={() => setDeleteModalOpen(false)}>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">Kalkylen och alla rader tas bort.</p>
            <div className="flex justify-end">
              <ToolbarButton className="bg-danger text-white" onClick={() => void deleteCalc()}>
                Ta bort
              </ToolbarButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
